import type { DateRange, Timeframe } from '#core/types/date-filter'
import { UTCDate } from '@date-fns/utc'
import { endOfDay, startOfDay, subDays } from 'date-fns'

/**
 * Date filter utils, which originally lived in Market Maker Monitoring
 */
const TIMEFRAME_IN_DAYS: Record<Timeframe, number> = {
  '24h': 1,
  '7d': 7,
  '30d': 30,
  '60d': 60,
}

export function getTimeframeInDays(timeframe: Timeframe): number {
  return TIMEFRAME_IN_DAYS[timeframe]
}

export function getTimeframeLabel(timeframe: Timeframe | 'custom'): string {
  if (timeframe === 'custom') {
    return ''
  }
  if (timeframe === '24h') {
    return '24 hours'
  }
  return `${getTimeframeInDays(timeframe)} days`
}

/**
 * Builds a date range for a given timeframe.
 *
 * - Operates on full calendar days (truncated to start of day).
 * - `end` is always yesterday (today excluded).
 * - `start` is `timeframeInDays` days before today.
 *
 * Examples:
 *   If today = 2025-09-03:
 *
 *   • timeframe = '7d'
 *       start = 2025-08-27 00:00:00
 *       end   = 2025-09-02 00:00:00
 *
 * @param timeframe - Logical timeframe key (e.g. '7d', '30d', '90d').
 * @param anchor - Reference "today"; the range ends the day before it. Defaults to now.
 * @returns An object with:
 *   - `start`: Start date of the range (Date, start of day).
 *   - `end`: End date of the range (Date, start of day, yesterday).
 *   - `timeframe`: The same timeframe argument for reference.
 */
export function getDateRange(timeframe: Timeframe, anchor?: Date): DateRange {
  const timeframeInDays = getTimeframeInDays(timeframe)
  const today = anchor ?? new UTCDate()
  const end = endOfDay(subDays(today, 1))
  const start = startOfDay(subDays(today, timeframeInDays))

  return {
    start,
    end,
    timeframe,
  }
}

/**
 * Generates a pagination label suffix based on the timeframe
 * @param timeframe - The timeframe to generate a label for
 * @returns A string like "30-day" for standard timeframes, or empty string for custom timeframes
 */
export function getPaginationTimeframeLabel(timeframe: Timeframe | 'custom') {
  if (timeframe === 'custom') {
    return ''
  }
  return `${getTimeframeInDays(timeframe)}-day`
}
